import { type ReactElement, useId } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Slider,
  type SliderProps,
  Typography,
} from "@mui/material";

import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type SliderInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label: string;
  hideLabel?: boolean;
  hideError?: boolean;
  hide?: boolean;
  control: Control<T>;
  decimals?: number;
  unit?: string;
} & SliderProps;

export const SliderInput = <T extends FieldValues>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  control,
  hide,
  min,
  max,
  step,
  decimals,
  unit,
  disabled,
}: SliderInputProps<T>): ReactElement | null => {
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}
    >
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth error={!!error} size={"small"}>
        <Box sx={{ display: "flex", gap: 2, padding: 0.75 }}>
          <Box
            sx={{
              borderRight: 1,
              borderRadius: 0,
              borderColor: "divider",
              padding: 1,
              paddingBlock: 0.5,
              minWidth: "68px",
              textAlign: "start",
            }}
          >
            <Typography>
              {(field.value ?? 0).toFixed(decimals ?? 0) + " " + (unit ?? "")}
            </Typography>
          </Box>
          <Slider
            size={"small"}
            slotProps={{
              input: {
                "aria-labelledby": hideLabel ? undefined : id,
                "aria-label": hideLabel ? label : undefined,
              },
            }}
            {...field}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            value={field.value ?? 0}
            valueLabelDisplay="auto"
          />
        </Box>
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
